import ComplexTable, { ComplexTableProps } from '@/components/ComplexTable';
import { ReactNode, forwardRef, useCallback } from 'react';
import { Validation, getProperty } from '@/utils';

import { CombineRule } from '@/models/gen/graphql';
import CombineRulesTableHeader from '@/features/Settings/components/pages/CombineRules/CombineRulesTable/CombineRulesTableHeader';
import CombineRulesTableRow from '@/features/Settings/components/pages/CombineRules/CombineRulesTable/CombineRulesTableRow';
import { ComplexFormItem } from '@/hooks/useComplexForm';

const COLUMN_SIZES = [
  '1.5fr', // Airline
  '1fr', // Created At
  '1fr', // Created By
  '0.75fr', // Pickup Window
  '1.25fr', // Discount
  '1.25fr', // Turnaround Time
  '0.75fr', // Illegal Combine
  '4rem', // Actions
];

export type CombineRulesTableProps = Omit<ComplexTableProps, 'columns' | 'header' | 'row'>;

export type CombineRuleMetadata = {
  id: string;
  isDirty: boolean;
  isValid: boolean;
  validity: {
    contractId: boolean;
    pickupWindow: boolean;
    turnaroundThreshold: boolean;
  };
};
const getMetadata = (item: CombineRule, { update }: { update: Record<string, unknown> }): CombineRuleMetadata => {
  const validity = {
    contractId: !!item?.contractId, //Validation.isValidUUID(item?.contractId),
    pickupWindow: Validation.isNumber(item?.pickupWindow) && Validation.isWithinThreshold(item?.pickupWindow, 0, 1440),
    turnaroundThreshold: Validation.isNumber(item?.turnaroundThreshold) && Validation.isWithinThreshold(item?.turnaroundThreshold, 0, 1440),
  };
  return {
    id: item?.id,
    isDirty: !!update,
    isValid: !item || !Object.values(validity).includes(false),
    validity,
  };
};

const CombineRulesTable = (props: CombineRulesTableProps, ref): ReactNode => {
  const sorting = useCallback((items: ComplexFormItem<CombineRule>[], col: number, dir: 'asc' | 'desc'): ComplexFormItem<CombineRule>[] => {
    if (col === undefined) return items;

    const column = [
      'data.provider.name', // Airline
      'data.creator.fullName', // Creator
      'data.createdAt', // Create At Time
      'data.pickupWindow', // Pickup Window
      'data.turnaroundThreshold', // Turnaround Time
    ][col];

    return items.sort(
      (a: ComplexFormItem<CombineRule>, b: ComplexFormItem<CombineRule>): number =>
        (getProperty(column, a) < getProperty(column, b) ? -1 : getProperty(column, a) > getProperty(column, b) ? 1 : 0) *
        (dir === 'desc' ? -1 : 1)
    );
  }, []);

  return (
    <ComplexTable
      {...props}
      columns={COLUMN_SIZES}
      header={CombineRulesTableHeader}
      row={CombineRulesTableRow}
      metadata={getMetadata}
      options={{ ...(props?.options || {}), sorting }}
      ref={ref}
    />
  );
};

export default forwardRef(CombineRulesTable);
